import {
  Grid,
  Typography,
  Select,
  MenuItem,
  Box,
  TextField,
  Paper,
  Alert,
  Button,
  Chip,
  SelectChangeEvent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Send as SendIcon,
  Refresh as RefreshIcon,
  History as HistoryIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import type { Application } from '../../../types/application';
import { AlertState, TemplateType } from '../types';
import { TEMPLATE_OPTIONS } from '../constants';
import { formatAlertDate } from '../utils';
import { biztalkTemplates } from '../../../../../../verify-functions/src/utils/biztalkTemplates';
import { AlertPreviewDialog } from '../../applicationDetail/dialogs/AlertPreviewDialog';
import AlertHistoryDetailDialog from './AlertHistoryDetailDialog';
import { useState } from 'react';

interface Props {
  application: Application;
  alertState: AlertState;
  alertPreviewMessage: string;
  isAlertFormValid: boolean;
  onTemplateSelect: (e: SelectChangeEvent<string>) => void;
  onRepairReasonChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onReturnTrackingChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSendClick: () => void;
  onConfirmSend: () => void;
  onSetConfirmDialog: (open: boolean) => void;
  onSetSnackbar: (open: boolean) => void;
}

const AlertTab = ({
  application,
  alertState,
  alertPreviewMessage,
  isAlertFormValid,
  onTemplateSelect,
  onRepairReasonChange,
  onReturnTrackingChange,
  onSendClick,
  onConfirmSend,
  onSetConfirmDialog,
  onSetSnackbar,
}: Props) => {
  // 🆕 A/B/C/D 등급별 색상
  const gradeChipSx = (grade?: string | null) => {
    switch (grade) {
      case 'A':
        return { bgcolor: '#10b981', color: '#fff' };
      case 'B':
        return { bgcolor: '#3b82f6', color: '#fff' };
      case 'C':
        return { bgcolor: '#f59e0b', color: '#fff' };
      case 'D':
        return { bgcolor: '#ef4444', color: '#fff' };
      default:
        return { bgcolor: '#90a4ae', color: '#fff' };
    }
  };
  // 전화번호 마스킹 유틸
  const maskPhone = (phone?: string) => {
    if (!phone) return '';
    if (phone.length < 7) return phone;
    return phone.slice(0, 3) + '****' + phone.slice(-4);
  };
  // 가격안내일 때 디바이스별 미리보기 생성
  const isPriceNotice = alertState.selectedType === '가격안내';
  const perDevicePreviews = isPriceNotice
    ? (application.devices || [])
        .filter(d => d.status === '가격안내')
        .map(d => {
          // 🆕 단일 '가격안내' 템플릿 사용
          const event = '가격안내';
          const template = biztalkTemplates[event];
          const priceStr = (d.price_offer || 0).toLocaleString();
          const applyDate = new Date((application.created_at as any)?.seconds ? (application.created_at as any).seconds * 1000 : Date.now()).toLocaleDateString('ko-KR');
          // 🆕 차감사유 라인: 값이 있을 때만 표시
          const deductionReasonLine = d.deduction_reason?.trim() 
            ? `차감사유 : ${d.deduction_reason.trim()}` 
            : '';
          const msg = template?.getMessage({
            userName: application.user_name,
            deviceName: d.deviceModel,
            applyDate,
            price: priceStr,
            application_id: application.id,
            device_id: d.id,
            // 🆕 가격안내 단일 템플릿용 파라미터
            grade: d.grade || '-',
            deductionReasonLine,
            applicationNumber: application.application_number?.toString() || application.id,
          }) || '';

          // 버튼/링크 사전 검증
          const errors: string[] = [];
          if (!template) errors.push('템플릿 누락');
          if (template?.buttons) {
            template.buttons.forEach(btn => {
              if (btn.type === 'WL') {
                if (!btn.url_mobile || !btn.url_pc) errors.push(`버튼 URL 누락: ${btn.name}`);
              }
            });
          } else {
            errors.push('버튼 정보 누락');
          }
          if (!d.price_offer && d.price_offer !== 0) errors.push('검수금액 누락');

          return {
            deviceId: d.id,
            deviceModel: d.deviceModel,
            grade: d.grade,
            priceStr,
            event,
            message: msg,
            errors,
          };
        })
    : [];

  // 🆕 차감사유 입력 확인 로직 제거 (모든 등급에서 선택적 입력)
  const missingRepairReasons: any[] = [];
  const needsGlobalRepairReason = false;

  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedLog, setSelectedLog] = useState<any>(null);

  return (
    <Grid container spacing={2}>
      {/* 템플릿 선택 */}
      <Grid item xs={12}>
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
          알림 템플릿 선택
        </Typography>
        <Select
          size="small"
          fullWidth
          value={alertState.selectedType}
          onChange={onTemplateSelect}
          displayEmpty
          sx={{ borderRadius: 2, bgcolor: '#f7fafd' }}
        >
          <MenuItem value="" disabled>템플릿을 선택하세요</MenuItem>
          {TEMPLATE_OPTIONS
            // 🆕 거래처 전용 템플릿은 거래처/수동 신청일 때만 표시
            .filter(option => {
              if (option.partnerOnly) {
                return application.source_type === 'partner' || application.source_type === 'manual';
              }
              return true;
            })
            .map((option) => (
            <MenuItem key={option.value} value={option.value} disabled={option.disabled}>
              <Box sx={{ opacity: option.disabled ? 0.6 : 1 }}>
                <Typography variant="body2" fontWeight={600}>
                  {option.label}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {option.description}
                </Typography>
              </Box>
            </MenuItem>
          ))}
        </Select>
      </Grid>

      {/* 사유 입력 (매입 불가 알림시) */}
      {(alertState.selectedType === '매입 불가') && (
        <Grid item xs={12}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
            {'❌ 매입 불가 사유 입력 (필수)'}
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            value={alertState.repairReason}
            onChange={onRepairReasonChange}
            placeholder={"매입이 불가능한 구체적인 사유를 입력하세요 (예: '기기 식별 불가', '심각한 파손', '액침 흔적' 등)"}
            sx={{ 
              '& .MuiOutlinedInput-root': { 
                borderRadius: 2, 
                bgcolor: '#ffebee',
                '&:hover': { bgcolor: '#ffebee' },
                '&.Mui-focused': { bgcolor: '#ffebee' }
              } 
            }}
            error={(alertState.selectedType === '매입 불가' && !alertState.repairReason.trim())}
            helperText={(!alertState.repairReason.trim() ? '사유 입력이 필수입니다' : '')}
          />
        </Grid>
      )}

      {/* 반송장번호 입력 (매입 불가, 반송 알림시) */}
      {(alertState.selectedType === '매입 불가' || alertState.selectedType === '반송') && (
        <Grid item xs={12}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
            📦 반송장번호 입력 {alertState.selectedType === '반송' ? '(필수)' : '(선택사항)'}
          </Typography>
          <TextField
            fullWidth
            value={alertState.returnTrackingNumber || ''}
            onChange={onReturnTrackingChange}
            placeholder="CJ대한통운 반송장번호를 입력하세요 (예: 123456789012)"
            sx={{ 
              '& .MuiOutlinedInput-root': { 
                borderRadius: 2, 
                bgcolor: alertState.selectedType === '반송' ? '#e8f5e8' : '#f3e5f5',
                '&:hover': { bgcolor: alertState.selectedType === '반송' ? '#e8f5e8' : '#f3e5f5' },
                '&.Mui-focused': { bgcolor: alertState.selectedType === '반송' ? '#e8f5e8' : '#f3e5f5' }
              } 
            }}
            error={alertState.selectedType === '반송' && !alertState.returnTrackingNumber?.trim()}
            helperText={
              alertState.selectedType === '반송' 
                ? (!alertState.returnTrackingNumber?.trim() ? '반송장번호 입력이 필수입니다' : '반송 완료 시 고객에게 전달할 반송장번호')
                : "반송장번호가 없으면 '처리 중'으로 표시됩니다"
            }
          />
        </Grid>
      )}

      {/* 메시지 미리보기 */}
      {isPriceNotice ? (
        <Grid item xs={12}>
          <Box sx={{
            mb: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            background: 'linear-gradient(90deg, #e3f2fd 0%, #f3e5f5 100%)',
            p: 1.5,
            borderRadius: 2,
            border: '1px solid #e1e8ed'
          }}>
            <Typography variant="subtitle2" fontWeight={800} sx={{ letterSpacing: '-0.2px' }}>
              💬 디바이스별 메시지 미리보기 ({perDevicePreviews.length}건)
            </Typography>
          </Box>
          {perDevicePreviews.length === 0 ? (
            <Paper elevation={0} sx={{ p: 2, bgcolor: '#fff3e0', borderRadius: 2, border: '1px solid #ffe0b2' }}>
              <Typography variant="body2" color="text.secondary">
                가격안내 상태의 디바이스가 없습니다.
              </Typography>
            </Paper>
          ) : (
            <Box sx={{ display: 'grid', gap: 1.5 }}>
              {perDevicePreviews.map((p) => (
                <Paper key={p.deviceId} elevation={0} sx={{ p: 1.5, borderRadius: 2, border: '1px solid #e1e8ed', bgcolor: 'rgba(250,252,255,0.9)', boxShadow: '0 4px 18px rgba(0,0,0,0.06)' }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="body2" fontWeight={800} sx={{ letterSpacing: '-0.2px' }}>
                      {p.deviceModel} · {p.priceStr}원
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Chip label={p.grade || '등급미정'} size="small" sx={{ height: 20, ...gradeChipSx(p.grade) }} />
                      <Chip label={p.event} size="small" sx={{ bgcolor: '#1976d2', color: '#fff', height: 20 }} />
                    </Box>
                  </Box>
                  {p.errors.length > 0 && (
                    <Box sx={{ mb: 1 }}>
                      {p.errors.map((e, idx) => (
                        <Chip key={idx} label={e} color="error" size="small" sx={{ mr: 0.5 }} />
                      ))}
                    </Box>
                  )}
                  <Paper elevation={0} sx={{ p: 1.5, borderRadius: 2, bgcolor: 'white', border: '1px solid #e1e8ed', boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.6)' }}>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-line', color: '#1565c0', lineHeight: 1.6 }}>
                      {p.message}
                    </Typography>
                  </Paper>
                </Paper>
              ))}
            </Box>
          )}
        </Grid>
      ) : (
        alertPreviewMessage && (
        <Grid item xs={12}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
            💬 메시지 미리보기
          </Typography>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              whiteSpace: 'pre-line',
              borderRadius: 2,
              background: '#f0f7ff',
              border: '1px solid #bbdefb',
            }}
          >
            <Typography variant="body2" sx={{ color: '#1565c0', lineHeight: 1.6 }}>
              {alertPreviewMessage}
            </Typography>
          </Paper>
        </Grid>
        )
      )}

      {/* 오류 메시지 */}
      {alertState.errorMessage && (
        <Grid item xs={12}>
          <Alert severity="error" sx={{ borderRadius: 2 }}>
            {alertState.errorMessage}
          </Alert>
        </Grid>
      )}

      {/* 전송 버튼 */}
      <Grid item xs={12}>
        <Button
          variant="contained"
          fullWidth
          onClick={onSendClick}
          disabled={!isAlertFormValid || alertState.sending}
          startIcon={alertState.sending ? <RefreshIcon /> : <SendIcon />}
          sx={{ borderRadius: 2, py: 1.5, fontWeight: 600 }}
        >
          {alertState.sending ? '전송 중...' : '알림톡 전송'}
        </Button>
      </Grid>

      {/* 📊 알림톡 전송 히스토리 */}
      <Grid item xs={12} sx={{ mt: 2 }}>
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5, display: 'flex', alignItems: 'center' }}>
          <HistoryIcon sx={{ mr: 1, fontSize: 18, color: '#1976d2' }} />
          전송 히스토리
        </Typography>
        
        {alertState.historyLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <RefreshIcon className="rotating" sx={{ color: '#1976d2' }} />
          </Box>
        ) : alertState.history.length === 0 ? (
          <Paper elevation={0} sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              아직 전송된 알림톡이 없습니다
            </Typography>
          </Paper>
        ) : (
          <Box sx={{ maxHeight: 320, overflowY: 'auto', pr: 0.5 }}>
            {alertState.history.map((log, index) => (
              <Paper
                key={index}
                elevation={0}
                sx={{
                  p: 1.5,
                  mb: 1,
                  borderRadius: 2,
                  position: 'relative',
                  borderLeft: `4px solid ${log.status === 'success' ? '#4caf50' : '#f44336'}`,
                  background: log.status === 'success'
                    ? 'linear-gradient(90deg, rgba(232,245,232,0.9) 0%, rgba(255,255,255,0.9) 100%)'
                    : 'linear-gradient(90deg, rgba(255,235,238,0.9) 0%, rgba(255,255,255,0.9) 100%)',
                  '&:last-child': { mb: 0 },
                  transition: 'transform 120ms ease, box-shadow 120ms ease',
                  '&:hover': { transform: 'translateY(-1px)', boxShadow: '0 4px 18px rgba(0,0,0,0.06)' },
                  cursor: 'pointer',
                }}
                onClick={() => {
                  setSelectedLog(log);
                  setDetailOpen(true);
                }}
              >
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" fontWeight={800} sx={{ mb: 0.25, letterSpacing: '-0.2px' }}>
                      {log.event || '알림톡'}
                      <Chip
                        label={log.status === 'success' ? '성공' : '실패'}
                        size="small"
                        sx={{
                          ml: 1,
                          bgcolor: log.status === 'success' ? '#4caf50' : '#f44336',
                          color: '#fff',
                          fontSize: '0.75rem',
                          height: 20,
                        }}
                      />
                    </Typography>
                    {(log.device_name || log.price != null || log.phone_number) && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
                        {[log.device_name, (log.price != null ? `${Number(log.price).toLocaleString()}원` : ''), maskPhone(log.phone_number)]
                          .filter(Boolean)
                          .join(' · ')}
                      </Typography>
                    )}
                    {log.error_message && (
                      <Typography variant="caption" color="error" sx={{ display: 'block', mb: 0.25 }}>
                        오류: {log.error_message}
                      </Typography>
                    )}
                    <Typography variant="caption" color="text.secondary">
                      {formatAlertDate(log.sent_at)}
                    </Typography>
                  </Box>
                  <Box sx={{ ml: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<HistoryIcon />}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedLog(log);
                        setDetailOpen(true);
                      }}
                      sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700 }}
                    >
                      상세 보기
                    </Button>
                    {log.status === 'success' ? (
                      <CheckCircleIcon sx={{ fontSize: 18, color: '#4caf50' }} />
                    ) : (
                      <WarningIcon sx={{ fontSize: 18, color: '#f44336' }} />
                    )}
                  </Box>
                </Box>
              </Paper>
            ))}
          </Box>
        )}
      </Grid>

      {/* 🔔 확인 다이얼로그 */}
      <AlertPreviewDialog
        open={alertState.confirmDialog}
        application={application}
        alertPreviewMessage={alertPreviewMessage}
        perDevicePreviews={perDevicePreviews}
        isStatusSaving={alertState.sending}
        onClose={() => onSetConfirmDialog(false)}
        onConfirm={async () => { onConfirmSend(); }}
      />

      {/* 🧾 전송 데이터 상세 다이얼로그 */}
      <AlertHistoryDetailDialog
        open={detailOpen}
        application={application}
        log={selectedLog}
        onClose={() => setDetailOpen(false)}
      />

      {/* 🎊 성공 스낵바 */}
      <Snackbar
        open={alertState.snackbar}
        autoHideDuration={4000}
        onClose={() => onSetSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => onSetSnackbar(false)} 
          severity="success"
          sx={{ borderRadius: 2 }}
        >
          알림톡 전송이 완료되었습니다! 📱
        </Alert>
      </Snackbar>
    </Grid>
  );
};

export default AlertTab; 